import _ from 'lodash';
import React, { useMemo } from 'react';
import { Field, useFormikContext } from 'formik';

import { FormikInput } from '../_core/Formik/formik-input.component';
import { FormikSelect } from '../_core/Formik/formik-select.component';
import { FormikCheckbox } from '../_core/Formik/formik-checkbox.component';
import { IFormOption } from '../_core/_ui/forms.component';
import { SPORT_INVOLVEMENT_OPTIONS, SPORT_TERMINOLOGY } from '../../lib/constants';
import { useContactDuplicates } from '../../lib/services/player-contact.service';
import { ContactDuplicateField, ContactRelationshipFormOptions, IPlayerContactCandidate, Sport } from '../../lib/types';
import { ContactDuplicateWarning } from './contact-duplicate-warning.component';
import { getPlayerContactMessages } from './player-contact.messages';

type ContactFieldValue = string | IFormOption | null | undefined;

const EMPTY_CONTACTS: IPlayerContactCandidate[] = [];

// A credential rendered as a picker holds an IFormOption; free text holds the
// string. Both reduce to the raw credential the redundancy check compares.
const credential = (value: ContactFieldValue): string | undefined =>
  (typeof value === 'string' ? value : value?.value as string) || undefined;

interface IPlayerContactFieldsProps {
  // Passed in, not read from the company store (only the staff route gate writes
  // it). null = no association picked yet: the sport-specific fields stay hidden.
  sport: Sport | null;
  // A parent's own name is account-level and cascades to every owned contact,
  // so their row editor takes it off the per-player form.
  showName?: boolean;
  // Staff choose the channels explicitly; the parent mutations derive them from
  // whether an email/phone was supplied, so the toggles would be inert there.
  showSendToggles?: boolean;
  // Contact height is staff-only until it moves to a milestone-style field.
  showHeight?: boolean;
  // Supplying options renders that credential as a constrained picker — a parent
  // may only point a player at a credential they have verified. Absent = free text.
  emailOptions?: IFormOption[];
  phoneOptions?: IFormOption[];
  // Formik path this contact sits at, for a form holding several — a create
  // player's `contacts[2]`. Absent means the fields are at the form root.
  namePrefix?: string;
  // The athlete's other contacts, which this block compares the entered values
  // against. Omitting it is what turns the redundancy banner off.
  siblingContacts?: IPlayerContactCandidate[];
  // Credentials this form's mutation rejects outright. The refusal names the
  // field, so the banner stays quiet rather than being a second voice on it.
  blockedFields?: ContactDuplicateField[];
}

export const PlayerContactFields: React.FC<IPlayerContactFieldsProps> = ({
  sport,
  showName = true,
  showSendToggles = true,
  showHeight = true,
  emailOptions,
  phoneOptions,
  namePrefix,
  siblingContacts,
  blockedFields,
}) => {
  // Terminology is a pure function of sport — the company store sets the two in
  // lockstep, so deriving it here keeps the one `sport` prop sufficient.
  const termLookup = SPORT_TERMINOLOGY[sport || Sport.default];
  const messages = getPlayerContactMessages(termLookup);

  const path = (field: string) => (namePrefix ? `${namePrefix}.${field}` : field);

  const { values } = useFormikContext<unknown>();
  const read = (field: string): ContactFieldValue => _.get(values, path(field));
  const candidate = { name: credential(read('name')), email: credential(read('email')), phone: credential(read('phone')) };

  const duplicates = useContactDuplicates(candidate, siblingContacts || EMPTY_CONTACTS);
  const advisory = useMemo(
    () => duplicates.filter((match) => !(blockedFields || []).includes(match.field)),
    [duplicates, blockedFields],
  );

  return (
    <>
      {showName && (
        <Field fid="cpcf" name={path('name')} margin="dense" component={FormikInput} label={messages.lblName} />
      )}
      <Field
        fid="cpf"
        name={path('relationship')}
        component={FormikSelect}
        label={messages.lblContactRelationship}
        options={ContactRelationshipFormOptions}
        isClearable
      />
      {sport === Sport.default && (
        <>
          {showHeight && (
            <Field
              margin="dense"
              fid="cpcf"
              name={path('height')}
              component={FormikInput}
              label={messages.lblContactHeight}
              type="number"
            />
          )}
          <Field
            fid="cpcf"
            name={path('sportInvolvement')}
            component={FormikSelect}
            label={messages.lblContactSportInvolvement}
            options={SPORT_INVOLVEMENT_OPTIONS}
            isClearable
          />
        </>
      )}
      {emailOptions
        ? (
          <Field
            fid="cpcf"
            name={path('email')}
            component={FormikSelect}
            label={messages.lblEmail}
            options={emailOptions}
          />
        ) : (
          <Field fid="cpcf" name={path('email')} margin="dense" component={FormikInput} label={messages.lblEmail} />
        )}
      {showSendToggles && (
        <Field fid="cpcf" id="cpcf_sendEmail" name={path('sendEmail')} margin="dense" component={FormikCheckbox} label={messages.lblSendEmail} />
      )}
      {phoneOptions
        ? (
          <Field
            fid="cpcf"
            name={path('phone')}
            component={FormikSelect}
            label={messages.lblPhone}
            options={phoneOptions}
          />
        ) : (
          <Field fid="cpcf" name={path('phone')} margin="dense" component={FormikInput} label={messages.lblPhone} />
        )}
      {showSendToggles && (
        <Field fid="cpcf" id="cpcf_sendSMS" name={path('sendSMS')} margin="dense" component={FormikCheckbox} label={messages.lblSendSMS} />
      )}

      <ContactDuplicateWarning matches={advisory} termLookup={termLookup} />
    </>
  );
};
